import { useState, useEffect } from "react";
import { useListContractClauses, useAddContractClause, useRemoveContractClause, useReorderContractClauses, useListClauses, useListContractClauseOverrides, useRequestContractLegalReview, useListContractNegotiationItems, getListContractClausesQueryKey, getListClausesQueryKey, getListContractClauseOverridesQueryKey, getListContractNegotiationItemsQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { BookOpen, Plus, Trash2, ChevronDown, ChevronUp, ExternalLink, ShieldAlert, Sparkles, ShieldCheck } from "lucide-react";
import { Link } from "wouter";
import { renderMergeFields, buildMergeContext, type ContractData } from "@/lib/mergeFields";
import { getGetContractComplianceQueryKey as getContractComplianceQueryKey } from "@workspace/api-client-react";
import { sanitizeHtml } from "@/lib/sanitize";
import {
  buildClauseEvalContext,
  getClauseInclusionStatus,
  type ClauseInclusionStatus,
} from "@/lib/clauseConditions";
import type { ClauseConditionExpr } from "@/components/ClauseConditionBuilder";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ViewModeToggle, type ViewMode } from "@/components/ViewModeToggle";
import { DrafterClauseView } from "@/components/DrafterClauseView";
import { AttorneyClauseView, type ContractClauseOverride } from "@/components/AttorneyClauseView";
import type { Persona } from "@/lib/orgContext";
import { useActivePersona } from "@/lib/activePersona";

const CATEGORY_LABELS: Record<string, string> = {
  compensation: "Compensation",
  termination: "Termination",
  buyout: "Buyout",
  nil: "NIL",
  title_ix: "Title IX",
  indemnification: "Indemnification",
  governing_law: "Governing Law",
  outside_income: "Outside Income",
  general: "General",
};

const STORAGE_KEY_PREFIX = "zindeal.contractViewMode";

// Attorney mode is reserved for users currently wearing their Legal hat.
// For multi-persona users (e.g. HR + Legal), this follows the active persona
// from the top-bar switcher instead of just whether Legal is in their list.
function getDefaultMode(isLegal: boolean): ViewMode {
  return isLegal ? "attorney" : "drafter";
}

function storageKeyFor(userId: string | null | undefined): string {
  return userId ? `${STORAGE_KEY_PREFIX}:${userId}` : STORAGE_KEY_PREFIX;
}

function getStoredMode(userId: string | null | undefined): ViewMode | null {
  try {
    const v = localStorage.getItem(storageKeyFor(userId));
    if (v === "drafter" || v === "attorney") return v;
  } catch { }
  return null;
}

interface ContractClausesProps {
  contractId: number;
  contractData: ContractData;
  isAdmin: boolean;
  personas?: Persona[];
  onClausesChanged?: () => void;
}

export function ContractClauses({ contractId, contractData, isAdmin, personas = [], onClausesChanged }: ContractClausesProps) {
  const { toast } = useToast();
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedClauseId, setSelectedClauseId] = useState<string>("");
  const [selectedVersionId, setSelectedVersionId] = useState<string>("");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Tie attorney mode to the *currently active* persona, not just whether
  // the user has Legal at all. A user with HR + Legal who is wearing their
  // HR hat should see drafter mode; switching back to Legal re-enables
  // attorney mode (and restores their per-user stored preference).
  const { effective: effectivePersona } = useActivePersona(personas);
  const isLegal = effectivePersona === "Legal";

  const defaultMode: ViewMode = isLegal
    ? (getStoredMode(userId) ?? getDefaultMode(isLegal))
    : "drafter";
  const [viewMode, setViewMode] = useState<ViewMode>(defaultMode);

  useEffect(() => {
    if (!isLegal) {
      if (viewMode !== "drafter") setViewMode("drafter");
    } else {
      // Returning to Legal: restore stored preference, defaulting to attorney.
      const restored = getStoredMode(userId) ?? "attorney";
      if (viewMode !== restored) setViewMode(restored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLegal, userId]);

  const handleModeChange = (m: ViewMode) => {
    if (!isLegal) return;
    setViewMode(m);
    try { localStorage.setItem(storageKeyFor(userId), m); } catch { }
  };

  const { data: contractClauses = [], isLoading } = useListContractClauses(contractId, {
    query: { queryKey: getListContractClausesQueryKey(contractId) },
  });

  const { data: libraryClauses = [] } = useListClauses(
    {},
    { query: { queryKey: getListClausesQueryKey({}) } }
  );

  const { data: overridesData = [], isLoading: overridesLoading } = useListContractClauseOverrides(contractId, {
    query: { queryKey: getListContractClauseOverridesQueryKey(contractId), enabled: !!contractId },
  });
  const overridesMap = new Map<number, ContractClauseOverride>();
  for (const o of overridesData as ContractClauseOverride[]) {
    overridesMap.set(o.contractClauseId, o);
  }

  type LibraryClauseWithCondition = (typeof libraryClauses)[number] & {
    inclusionCondition?: ClauseConditionExpr | null;
    inclusionRequired?: boolean | null;
  };

  const evalCtx = buildClauseEvalContext(contractData);
  const requestLegalReview = useRequestContractLegalReview();
  const { data: negotiationItems = [] } = useListContractNegotiationItems(contractId, {
    query: {
      queryKey: getListContractNegotiationItemsQueryKey(contractId),
      enabled: !!contractId,
    },
  });

  const handleOverrideChange = (_contractClauseId: number, _override: ContractClauseOverride | null) => {
    queryClient.invalidateQueries({ queryKey: getListContractClauseOverridesQueryKey(contractId) });
    queryClient.invalidateQueries({ queryKey: getContractComplianceQueryKey(contractId) });
  };

  const lockedCount = Array.from(overridesMap.values()).filter((o) => o.isLocked).length;

  const handleRequestLegalReview = async () => {
    try {
      const body = await requestLegalReview.mutateAsync({ id: contractId });
      const count = body.lockedCount ?? lockedCount;
      toast({ title: "Legal review requested", description: `Your request has been recorded. Legal will be notified about ${count} locked clause${count === 1 ? "" : "s"}.` });
    } catch (err) {
      const data =
        err && typeof err === "object" && "data" in err
          ? (err as { data: unknown }).data
          : null;
      const description =
        data && typeof data === "object" && "error" in data && typeof (data as { error?: unknown }).error === "string"
          ? (data as { error: string }).error
          : "Could not send review request.";
      toast({ title: "Could not request review", description, variant: "destructive" });
    }
  };

  const addClause = useAddContractClause();
  const removeClause = useRemoveContractClause();
  const reorderClauses = useReorderContractClauses();

  const mergeContext = buildMergeContext(contractData);
  const selectedLibraryClause = libraryClauses.find((c) => c.id === Number(selectedClauseId));

  const handleAdd = async () => {
    if (!selectedClauseId || !selectedVersionId) return;
    try {
      await addClause.mutateAsync({
        contractId,
        data: {
          clauseId: Number(selectedClauseId),
          clauseVersionId: Number(selectedVersionId),
        },
      });
      toast({ title: "Clause added", description: "The clause has been attached to this contract." });
      queryClient.invalidateQueries({ queryKey: getListContractClausesQueryKey(contractId) });
      queryClient.invalidateQueries({ queryKey: getContractComplianceQueryKey(contractId) });
      setAddDialogOpen(false);
      setSelectedClauseId("");
      setSelectedVersionId("");
      onClausesChanged?.();
    } catch {
      toast({ title: "Error", description: "Could not attach clause.", variant: "destructive" });
    }
  };

  const handleQuickAdd = async (clauseId: number, clauseVersionId: number, title: string) => {
    try {
      await addClause.mutateAsync({
        contractId,
        data: { clauseId, clauseVersionId },
      });
      toast({ title: "Clause added", description: `"${title}" has been attached to this contract.` });
      queryClient.invalidateQueries({ queryKey: getListContractClausesQueryKey(contractId) });
      queryClient.invalidateQueries({ queryKey: getContractComplianceQueryKey(contractId) });
      onClausesChanged?.();
    } catch {
      toast({ title: "Error", description: "Could not attach clause.", variant: "destructive" });
    }
  };

  const handleRemove = async (id: number) => {
    try {
      await removeClause.mutateAsync({ contractId, id });
      toast({ title: "Clause removed" });
      queryClient.invalidateQueries({ queryKey: getListContractClausesQueryKey(contractId) });
      queryClient.invalidateQueries({ queryKey: getContractComplianceQueryKey(contractId) });
      onClausesChanged?.();
    } catch {
      toast({ title: "Error", description: "Could not remove clause.", variant: "destructive" });
    }
  };

  const moveClause = async (index: number, direction: "up" | "down") => {
    const newOrder = [...contractClauses];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    try {
      await reorderClauses.mutateAsync({
        contractId,
        data: {
          order: newOrder.map((cc, i) => ({ id: cc.id, position: i })),
        },
      });
      queryClient.invalidateQueries({ queryKey: getListContractClausesQueryKey(contractId) });
    } catch {
      toast({ title: "Error", description: "Could not reorder clauses.", variant: "destructive" });
    }
  };

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clauses = contractClauses as Array<{
    id: number;
    contractId: number;
    position: number;
    clause: { id: number; title: string; category: string; jurisdiction: string | null; tags: string[]; isSeed: boolean; };
    version: { id: number; versionNumber: number; body: string; createdBy: string | null; };
  }>;

  const attachedClauseIds = new Set(clauses.map((cc) => cc.clause.id));

  const libraryWithStatus = (libraryClauses as LibraryClauseWithCondition[]).map((c) => ({
    clause: c,
    status: getClauseInclusionStatus(
      { inclusionCondition: c.inclusionCondition, inclusionRequired: c.inclusionRequired },
      evalCtx,
    ) as ClauseInclusionStatus,
    attached: attachedClauseIds.has(c.id),
  }));

  const missingRequired = libraryWithStatus.filter((x) => x.status === "required" && !x.attached);
  const missingSuggested = libraryWithStatus.filter((x) => x.status === "suggested" && !x.attached);

  const handleAddAllRequired = async () => {
    let success = 0;
    let failed = 0;
    for (const m of missingRequired) {
      const versionId = m.clause.latestVersion?.id;
      if (!versionId) { failed++; continue; }
      try {
        await addClause.mutateAsync({
          contractId,
          data: { clauseId: m.clause.id, clauseVersionId: versionId },
        });
        success++;
      } catch {
        failed++;
      }
    }
    queryClient.invalidateQueries({ queryKey: getListContractClausesQueryKey(contractId) });
    onClausesChanged?.();
    if (failed === 0) {
      toast({
        title: "Required clauses added",
        description: `Attached ${success} required clause${success === 1 ? "" : "s"} to this contract.`,
      });
    } else if (success === 0) {
      toast({
        title: "Could not add required clauses",
        description: `${failed} clause${failed === 1 ? "" : "s"} could not be attached.`,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Some required clauses added",
        description: `Attached ${success}, failed to attach ${failed}.`,
        variant: "destructive",
      });
    }
  };

  const sortedLibraryForPicker = [...libraryWithStatus].sort((a, b) => {
    const rank = (s: ClauseInclusionStatus, attached: boolean): number => {
      if (attached) return 4;
      if (s === "required") return 0;
      if (s === "suggested") return 1;
      return 2;
    };
    const ra = rank(a.status, a.attached);
    const rb = rank(b.status, b.attached);
    if (ra !== rb) return ra - rb;
    return a.clause.title.localeCompare(b.clause.title);
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-muted-foreground" /> Legal Clauses
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {isLegal && <ViewModeToggle mode={viewMode} onChange={handleModeChange} />}
            <Button variant="outline" size="sm" asChild>
              <Link href="/clause-library">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Clause Library
              </Link>
            </Button>
            {isAdmin && (viewMode === "drafter" || isLegal) && (
              <Button size="sm" onClick={() => setAddDialogOpen(true)}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Clause
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!isLoading && isAdmin && missingRequired.length > 0 && (
          <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/20 px-4 py-3 mb-4">
            <ShieldCheck className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-red-900 dark:text-red-200">
                {missingRequired.length} required clause{missingRequired.length === 1 ? "" : "s"} missing for this contract
              </div>
              <div className="text-xs text-red-800/80 dark:text-red-300/80 mt-0.5">
                {missingRequired.slice(0, 3).map((m) => m.clause.title).join(", ")}
                {missingRequired.length > 3 ? `, +${missingRequired.length - 3} more` : ""}
              </div>
            </div>
            <Button
              size="sm"
              className="shrink-0 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleAddAllRequired}
              disabled={addClause.isPending}
              data-testid="button-add-required-clauses"
            >
              {addClause.isPending ? "Adding…" : `Add all required`}
            </Button>
          </div>
        )}
        {!isLoading && isAdmin && missingRequired.length === 0 && missingSuggested.length > 0 && (
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20 px-4 py-3 mb-4">
            <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {missingSuggested.length} suggested clause{missingSuggested.length === 1 ? "" : "s"} match this contract
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {missingSuggested.slice(0, 5).map((m) => (
                  <Button
                    key={m.clause.id}
                    size="sm"
                    variant="outline"
                    className="h-6 text-xs border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/40"
                    onClick={() => {
                      const versionId = m.clause.latestVersion?.id;
                      if (versionId) handleQuickAdd(m.clause.id, versionId, m.clause.title);
                    }}
                    disabled={addClause.isPending || !m.clause.latestVersion?.id}
                    data-testid={`button-add-suggested-${m.clause.id}`}
                  >
                    <Plus className="h-3 w-3 mr-1" /> {m.clause.title}
                  </Button>
                ))}
                {missingSuggested.length > 5 && (
                  <span className="text-xs text-amber-800/80 dark:text-amber-300/80 self-center">
                    +{missingSuggested.length - 5} more in picker
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        {!isLoading && !overridesLoading && !isLegal && lockedCount > 0 && (
          <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20 px-4 py-3 mb-4">
            <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-300 flex-1">
              {lockedCount} clause{lockedCount === 1 ? " is" : "s are"} locked by Legal. Changes to affected contract terms require Legal sign-off.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/40"
              onClick={handleRequestLegalReview}
              disabled={requestLegalReview.isPending}
            >
              {requestLegalReview.isPending ? "Requesting…" : "Request Legal Review"}
            </Button>
          </div>
        )}
        {isLoading || overridesLoading ? (
          <div className="text-sm text-muted-foreground animate-pulse py-6 text-center">Loading clauses…</div>
        ) : viewMode === "drafter" ? (
          <DrafterClauseView
            clauses={clauses}
            overrides={overridesMap}
            contractData={contractData}
            negotiationItems={negotiationItems}
            onSwitchToAttorney={() => handleModeChange("attorney")}
          />
        ) : (
          <div className="space-y-4">
            {isAdmin && isLegal && contractClauses.length > 0 && (
              <div className="text-xs text-muted-foreground flex items-center gap-3 pb-1 border-b">
                <span>Reorder clauses:</span>
                {clauses.map((cc, index) => (
                  <span key={cc.id} className="flex items-center gap-0.5">
                    <button onClick={() => moveClause(index, "up")} disabled={index === 0} className="p-0.5 rounded hover:bg-muted disabled:opacity-30">
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <span className="text-xs">{cc.clause.title.slice(0, 15)}{cc.clause.title.length > 15 ? "…" : ""}</span>
                    <button onClick={() => moveClause(index, "down")} disabled={index === clauses.length - 1} className="p-0.5 rounded hover:bg-muted disabled:opacity-30">
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <AttorneyClauseView
              clauses={clauses}
              overrides={overridesMap}
              contractData={contractData}
              contractId={contractId}
              isAdmin={isAdmin}
              isLegal={isLegal}
              negotiationItems={negotiationItems}
              onOverrideChange={handleOverrideChange}
            />
            {isAdmin && isLegal && contractClauses.length > 0 && (
              <div className="border-t pt-3 space-y-1">
                <div className="text-xs font-medium text-muted-foreground">Remove clauses</div>
                {clauses.map((cc) => (
                  <div key={cc.id} className="flex items-center gap-2">
                    <span className="text-xs flex-1">{cc.clause.title}</span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-destructive hover:bg-destructive/10">
                          <Trash2 className="h-3 w-3 mr-1" /> Remove
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove clause?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This removes "{cc.clause.title}" from this contract. The clause remains in the library.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleRemove(cc.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!isLoading && contractClauses.length === 0 && (
          <div className="py-10 text-center space-y-3">
            <BookOpen className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground">No clauses attached to this contract yet.</p>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => setAddDialogOpen(true)}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add from Library
              </Button>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={addDialogOpen} onOpenChange={(open) => { setAddDialogOpen(open); if (!open) { setSelectedClauseId(""); setSelectedVersionId(""); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Clause from Library</DialogTitle>
            <DialogDescription>
              Choose a clause and version to attach to this contract. Merge fields will resolve to this contract's values.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Clause</label>
              <Select
                value={selectedClauseId}
                onValueChange={(val) => {
                  setSelectedClauseId(val);
                  const clause = libraryClauses.find((c) => c.id === Number(val));
                  if (clause?.latestVersion) {
                    setSelectedVersionId(String(clause.latestVersion.id));
                  } else {
                    setSelectedVersionId("");
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a clause…" />
                </SelectTrigger>
                <SelectContent>
                  {sortedLibraryForPicker.map(({ clause: c, status, attached }) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <span className="inline-flex items-center gap-1.5">
                        {status === "required" && (
                          <Badge className="text-[10px] h-4 px-1.5 bg-red-100 text-red-700 border-red-200" data-testid={`badge-required-${c.id}`}>Required</Badge>
                        )}
                        {status === "suggested" && (
                          <Badge className="text-[10px] h-4 px-1.5 bg-amber-100 text-amber-700 border-amber-200" data-testid={`badge-suggested-${c.id}`}>Suggested</Badge>
                        )}
                        {attached && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5 text-muted-foreground">Attached</Badge>
                        )}
                        <span>{c.title}</span>
                        <span className="text-muted-foreground">({CATEGORY_LABELS[c.category] ?? c.category})</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedLibraryClause && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Version</label>
                <Select value={selectedVersionId} onValueChange={setSelectedVersionId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select version…" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedLibraryClause.latestVersion && (
                      <SelectItem value={String(selectedLibraryClause.latestVersion.id)}>
                        v{selectedLibraryClause.latestVersion.versionNumber} (latest)
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedLibraryClause?.latestVersion && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1.5">Preview (with merged values)</div>
                <div
                  className="text-xs leading-relaxed p-3 rounded-md border bg-muted/30 max-h-40 overflow-y-auto prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(renderMergeFields(selectedLibraryClause.latestVersion.body, mergeContext)),
                  }}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={!selectedClauseId || !selectedVersionId || addClause.isPending}>
              {addClause.isPending ? "Adding…" : "Add Clause"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
